import {
  collection,
  CollectionReference,
  doc,
  DocumentReference,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { collectionData, docData } from 'rxfire/firestore';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import {
  Entity,
  EntityType,
  IAthlete,
  IAthleteEntity,
  IGroup,
  IModelEntity,
  IOptionGroup,
  IPermission,
  ISelectOption,
  ITeam,
  IUser,
} from '../types';
import {
  ISelectedEntitySettings,
  selectedEntitySettingsProvider,
} from './settings/selectedEntitySettingsProvider';
import { authenticatedUserProvider } from './userProvider';

export interface IEntities {
  athletes: IAthlete[];
  teams: ITeam[];
  groups: IGroup[];
  settings: ISelectedEntitySettings;
  selected: {
    team: ITeam;
    group: IGroup;
    athlete: IAthlete;
  };
}

/**
 * @description subjects for each entity type (in memory cache)
 */
const athletes$ = new BehaviorSubject<IAthlete[]>(null);
const teams$ = new BehaviorSubject<ITeam[]>(null);
const groups$ = new BehaviorSubject<IGroup[]>(null);

/**
 * @description observable all the entities
 */
export const archivedEntitiesProvider = (): Observable<IEntities> => {
  return combineLatest([
    teams$.pipe(filter((val: ITeam[]): boolean => !!val)),
    groups$.pipe(filter((val: IGroup[]): boolean => !!val)),
    athletes$.pipe(filter((val: IAthlete[]): boolean => !!val)),
    selectedEntitySettingsProvider(),
  ]).pipe(
    map(
      (
        result: [ITeam[], IGroup[], IAthlete[], ISelectedEntitySettings]
      ): IEntities => {
        const [mTeams, mGroups, mAthletes, settings] = result;
        return {
          athletes: mAthletes,
          groups: mGroups,
          teams: mTeams,
          settings,
          selected: createSelectedEntities(
            mTeams,
            mGroups,
            mAthletes,
            settings
          ),
        };
      }
    )
  );
};

const createSelectedEntities = (
  mTeams: ITeam[],
  mGroups: IGroup[],
  mAthletes: IAthlete[],
  settings: ISelectedEntitySettings
): IEntities['selected'] => {
  return {
    team: findSelected(settings?.teamId, mTeams),
    group: findSelected(settings?.groupId, mGroups),
    athlete: findSelected(settings?.athleteId, mAthletes),
  };
};

const findSelected = (id: string, list: IModelEntity[]): IModelEntity => {
  if (list.length === 0) {
    return null;
  }
  const selectedEntity = list.find((e: IModelEntity): boolean => e.id === id);
  if (Boolean(selectedEntity)) {
    return selectedEntity;
  } else {
    return list[0];
  }
};

/**
 * @description subscribes to all the teams the user has access to
 *
 * @param user
 */
const teams = (user: IUser): Observable<ITeam[]> => {
  if (isEmpty(user)) return EMPTY;
  if (user.claims.orgAdmin) {
    return collectionData<ITeam>(
      query(
        collection(
          user.services.firestore,
          `orgs/${user.orgId}/teams`
        ) as CollectionReference<ITeam>,
        orderBy('name')
      ),
      { idField: 'id' }
    ).pipe(
      map((mTeams: ITeam[]): ITeam[] =>
        sortBy(mTeams, (team: ITeam): number => team.priority)
      )
    );
  } else {
    if (!user.permissions.length) return EMPTY;
    const obs: Array<Observable<ITeam>> = user.permissions.map(
      (perm: IPermission): Observable<ITeam> =>
        docData(
          doc(
            user.services.firestore,
            `orgs/${user.orgId}/teams/${perm.id}`
          ) as DocumentReference<ITeam>
        )
    );
    if (!obs.length) return EMPTY;
    return combineLatest(obs).pipe(
      map((mTeams: ITeam[]): ITeam[] =>
        sortBy(mTeams, (team: ITeam): number => team.priority)
      )
    );
  }
};

/**
 * @description subscribes to all the groups the user has access to
 * (groups that are members of a team the user has been assigned)
 * @param user
 */
const groups = (user: IUser): Observable<IGroup[]> => {
  if (isEmpty(user)) return EMPTY;
  if (user.claims.orgAdmin) {
    return collectionData(
      query(
        collection(
          user.services.firestore,
          `orgs/${user.orgId}/groups`
        ) as CollectionReference<IGroup>,
        where('active', '==', false),
        orderBy('name')
      ),
      { idField: 'id' }
    );
  } else {
    if (user.teams.length <= 10) {
      return collectionData(
        query(
          collection(
            user.services.firestore,
            `orgs/${user.orgId}/groups`
          ) as CollectionReference<IGroup>,
          where('active', '==', false),
          where('teams', 'array-contains-any', user.teams),
          orderBy('name')
        ),
        { idField: 'id' }
      );
    } else {
      const teamBatches = chunk(user.teams, 10);
      const queries = teamBatches.map(
        (batch: string[]): Observable<IGroup[]> => {
          return collectionData(
            query(
              collection(
                user.services.firestore,
                `orgs/${user.orgId}/groups`
              ) as CollectionReference<IGroup>,
              where('active', '==', false),
              where('teams', 'array-contains-any', batch),
              orderBy('name')
            ),
            { idField: 'id' }
          );
        }
      );
      return combineLatest(queries).pipe(
        map((result: IGroup[][]): IGroup[] =>
          uniqBy(flatten(result), (entity: IGroup): string => entity.id)
        )
      );
    }
  }
};

/**
 * @description subscribes to all the athletes the user has access to
 * (athletes that are members of a team the user has been assigned)
 * @param user
 */
const athletes = (user: IUser): Observable<IAthlete[]> => {
  let outputObs$: Observable<IAthleteEntity[]>;
  if (isEmpty(user)) return EMPTY;
  if (user.claims.orgAdmin) {
    const ref = query(
      collection(
        user.services.firestore,
        `orgs/${user.orgId}/athletes`
      ) as CollectionReference<IAthleteEntity>,
      where('active', '==', false),
      orderBy('name', 'asc')
    );
    outputObs$ = collectionData(ref, { idField: 'id' });
  } else {
    if (user.teams.length <= 10) {
      const ref = query(
        collection(
          user.services.firestore,
          `orgs/${user.orgId}/athletes`
        ) as CollectionReference<IAthleteEntity>,
        where('teams', 'array-contains-any', user.teams),
        where('active', '==', false),
        orderBy('name', 'asc')
      );
      outputObs$ = collectionData(ref, { idField: 'id' });
    } else {
      const teamBatches = chunk(user.teams, 10);
      const queries = teamBatches.map(
        (batch: string[]): Observable<IAthleteEntity[]> => {
          const ref = query(
            collection(
              user.services.firestore,
              `orgs/${user.orgId}/athletes`
            ) as CollectionReference<IAthleteEntity>,
            where('teams', 'array-contains-any', batch),
            where('active', '==', false),
            orderBy('name', 'asc')
          );
          return collectionData(ref, { idField: 'id' });
        }
      );
      outputObs$ = combineLatest(queries).pipe(
        map((result: IAthleteEntity[][]): IAthleteEntity[] =>
          uniqBy(flatten(result), (entity: IAthleteEntity): string => entity.id)
        )
      );
    }
  }
  return outputObs$.pipe(
    map((mAthletes: IAthleteEntity[]): IAthlete[] => {
      return mAthletes.map((athlete: IAthleteEntity): IAthlete => {
        const athleteModel = athlete as unknown as IAthlete;
        /// extract external properties if they exist
        if (athleteModel?.external?.[user.orgId]) {
          athleteModel.external = athlete?.external?.[user.orgId];
        }
        return athleteModel;
      });
    })
  );
};

/**
 * @description start the flow of groups
 */
authenticatedUserProvider().pipe(switchMap(groups)).subscribe(groups$);

/**
 * @description start the flow of teams
 */
authenticatedUserProvider().pipe(switchMap(teams)).subscribe(teams$);

/**
 * @description start the flow of athletes
 */
authenticatedUserProvider().pipe(switchMap(athletes)).subscribe(athletes$);

/**
 * @description operator function to convert all the entities to grouped select options
 * @param entities
 */
export const toGroupedSelectOptions = (entities: IEntities): IOptionGroup[] => {
  const teamOptions = {
    options: entities.teams?.map(
      (val: ITeam): ISelectOption => toOption(val, 'teams')
    ),
    label: 'Teams',
  };

  const groupOptions = {
    options: entities.groups?.map(
      (val: ITeam): ISelectOption => toOption(val, 'groups')
    ),
    label: 'Groups',
  };

  const athleteOptions = groupByParentEntity(
    'athletes',
    entities.teams,
    entities.athletes,
    'teams'
  );

  return [teamOptions, groupOptions, ...athleteOptions];
};

export type GroupedEntityRelationship = 'groups' | 'teams';

/**
 * @description groups items by a parent item
 * @param parents - the list of possible group sections
 * @param entities - the list of items to group
 * @param relationship - the property on the item where the relationship is defined
 */
export const groupByParentEntity = (
  entityType: EntityType,
  parents: Entity[],
  entities: Entity[],
  relationship: GroupedEntityRelationship
): IOptionGroup[] => {
  const items: IOptionGroup[] = [];
  for (const parent of parents) {
    const grouping: ISelectOption[] = [];
    for (const entity of entities) {
      if (entity[relationship]?.includes(parent.id)) {
        grouping.push(toOption(entity, entityType));
      }
    }
    if (grouping.length > 0) {
      items.push({
        label: parent.name,
        options: grouping,
      });
    }
  }
  return items;
};

/**
 * @description creates a select options from an entity
 * @param entity
 * @param entityType
 */
const toOption = (entity: Entity, entityType: EntityType): ISelectOption => {
  return {
    label: entity.name,
    value: `${entityType}:${entity.id}`,
  };
};
